import { z } from 'zod';
import { CLASSES } from './classes.js';

export const ARTIFACT_TYPES = ['quest', 'item', 'npc', 'object', 'zone', 'ability', 'recipe'] as const;
export type ArtifactType = (typeof ARTIFACT_TYPES)[number];
export const artifactTypeSchema = z.enum(ARTIFACT_TYPES);

/**
 * Provenance of an artifact's live data: seeded from the Classic Era dump, read from a WoW:
 * Forever client build (`sourceBuild` names it), verified in Forever by a player, or edited
 * by a player without verification.
 */
export const DATA_STATUSES = [
  'classic_baseline',
  'forever_beta',
  'forever_confirmed',
  'player_submitted',
] as const;
export type DataStatus = (typeof DATA_STATUSES)[number];
export const dataStatusSchema = z.enum(DATA_STATUSES);

/** `addon`: created from a Thottbot addon upload (no seed source knew the game id). */
export const SOURCES = ['classicdb', 'blizzard_api', 'fixture', 'player', 'addon'] as const;
export type Source = (typeof SOURCES)[number];

export const ROLES = ['player', 'moderator', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const SUBMISSION_KINDS = ['create', 'edit'] as const;
export type SubmissionKind = (typeof SUBMISSION_KINDS)[number];
export const SUBMISSION_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export const DATA_STATUS_LABELS: Record<DataStatus, string> = {
  classic_baseline: 'Classic Era baseline',
  forever_beta: 'Forever beta',
  forever_confirmed: 'Forever confirmed',
  player_submitted: 'Player submitted',
};

/** Badge text: Forever beta data names the client build it was read from. */
export function formatDataStatus(status: DataStatus, build?: string | null): string {
  const label = DATA_STATUS_LABELS[status];
  return status === 'forever_beta' && build ? `${label} (${build})` : label;
}

/** Numeric comparison of client build strings (`major.minor.patch.build`): <0, 0 or >0. */
export function compareBuilds(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export const ARTIFACT_TYPE_LABELS: Record<ArtifactType, { singular: string; plural: string }> = {
  quest: { singular: 'Quest', plural: 'Quests' },
  item: { singular: 'Item', plural: 'Items' },
  npc: { singular: 'NPC', plural: 'NPCs' },
  object: { singular: 'Object', plural: 'Objects' },
  zone: { singular: 'Zone', plural: 'Zones' },
  ability: { singular: 'Class ability', plural: 'Class abilities' },
  recipe: { singular: 'Recipe', plural: 'Recipes' },
};

/**
 * A reference to another artifact. Seeded links carry the game id (resolved to an
 * artifact id in a final seed pass); player links carry the artifact id directly.
 */
const shortText = z.string().max(200);
const longText = z.string().max(8000);
const nonNegInt = z.number().int().min(0);

export const artifactRefSchema = z.object({
  artifactId: z.string().uuid().optional(),
  gameId: z.number().int().optional(),
  name: z.string().min(1).max(200),
  /** Target type when the key alone is ambiguous (a quest giver may be an NPC or an object). */
  refType: artifactTypeSchema.optional(),
  /** Addon observations: times seen, the source's loot-event denominator, vendor price (copper). */
  count: nonNegInt.optional(),
  looted: nonNegInt.optional(),
  price: nonNegInt.optional(),
  /** Client build the link was last observed in. */
  build: shortText.optional(),
});
export type ArtifactRef = z.infer<typeof artifactRefSchema>;

/** `{ uploads, build }`: how many addon uploads built the record and the newest build among them. */
const observedSchema = z.object({ uploads: nonNegInt, build: shortText }).strict();

export const questDataSchema = z
  .object({
    level: nonNegInt.max(70).optional(),
    minLevel: nonNegInt.max(70).optional(),
    zone: shortText.optional(),
    faction: z.enum(['Alliance', 'Horde', 'Both']).optional(),
    giver: artifactRefSchema.optional(),
    turnIn: artifactRefSchema.optional(),
    objectives: longText.optional(),
    description: longText.optional(),
    rewards: z
      .object({
        xp: nonNegInt.optional(),
        money: nonNegInt.optional(),
        items: z.array(artifactRefSchema).max(20).optional(),
        choiceItems: z.array(artifactRefSchema).max(20).optional(),
      })
      .optional(),
    prevQuest: artifactRefSchema.optional(),
    nextQuest: artifactRefSchema.optional(),
    /** Text shown while the quest is incomplete / on turn-in (addon-observed). */
    progressText: longText.optional(),
    completionText: longText.optional(),
    observed: observedSchema.optional(),
  })
  .strict();

export const itemDataSchema = z
  .object({
    quality: z.number().int().min(0).max(6).optional(),
    itemClass: shortText.optional(),
    subclass: shortText.optional(),
    slot: shortText.optional(),
    itemLevel: nonNegInt.optional(),
    requiredLevel: nonNegInt.max(70).optional(),
    bind: z.enum(['None', 'BoP', 'BoE', 'BoU', 'Quest']).optional(),
    stats: z.array(z.object({ name: shortText, value: z.number().int() })).max(20).optional(),
    armor: nonNegInt.optional(),
    damage: z.object({ min: nonNegInt, max: nonNegInt, speed: z.number().positive() }).optional(),
    sellPrice: nonNegInt.optional(),
    iconUrl: z.string().url().optional(),
    description: longText.optional(),
    droppedBy: z.array(artifactRefSchema.extend({ chance: z.number().min(0).max(100).optional() })).max(200).optional(),
    soldBy: z.array(artifactRefSchema).max(200).optional(),
    /** Classes and races that may equip the item (client AllowableClass / AllowableRace; absent = everyone). */
    classes: z.array(z.enum(CLASSES)).max(9).optional(),
    races: z.array(shortText).max(12).optional(),
    /** Profession-only gear: the profession and skill rank it requires. */
    requiredProfession: z.object({ name: shortText, skill: nonNegInt }).optional(),
    /** Classic PvP rank the item required (from the dump's `requiredhonorrank`). */
    pvpRank: z.number().int().min(1).max(14).optional(),
    /** A consumable's use effect as the client states it (seed-managed). */
    use: z
      .object({
        text: longText,
        duration: shortText.optional(),
        cooldown: shortText.optional(),
        effects: z.array(z.object({ kind: shortText, name: shortText, value: z.number().int().optional() })).max(8),
        /** Weapon oils and sharpening stones: the temporary enchantment they apply. */
        enchantId: nonNegInt.optional(),
      })
      .optional(),
    observed: observedSchema.optional(),
  })
  .strict();

export const locationSchema = z.object({
  map: z.number().int(),
  x: z.number(),
  y: z.number(),
  /** Addon sightings clustered into this spawn point. */
  count: nonNegInt.optional(),
});

export const npcDataSchema = z
  .object({
    levelMin: nonNegInt.max(70).optional(),
    levelMax: nonNegInt.max(70).optional(),
    creatureType: shortText.optional(),
    faction: shortText.optional(),
    zone: shortText.optional(),
    description: longText.optional(),
    /** Client creature display id from the dump; keys Blizzard's portrait renders (seed-managed). */
    displayId: nonNegInt.optional(),
    /** Portrait copied from Blizzard's creature-display media into our image storage (seed-managed). */
    imageUrl: z.string().url().optional(),
    locations: z.array(locationSchema).max(500).optional(),
    drops: z.array(artifactRefSchema.extend({ chance: z.number().min(0).max(100).optional() })).max(500).optional(),
    /** Loot events over addon uploads: the denominator of `drops[].chance`. */
    lootedCount: nonNegInt.optional(),
    startsQuests: z.array(artifactRefSchema).max(100).optional(),
    endsQuests: z.array(artifactRefSchema).max(100).optional(),
    sells: z.array(artifactRefSchema).max(500).optional(),
    /** Class abilities a trainer teaches (seed-managed, resolved to ability artifacts). */
    teaches: z.array(artifactRefSchema).max(200).optional(),
    observed: observedSchema.optional(),
  })
  .strict();

export const objectDataSchema = z
  .object({
    objectType: shortText.optional(),
    zone: shortText.optional(),
    description: longText.optional(),
    locations: z.array(locationSchema).max(500).optional(),
    contains: z.array(artifactRefSchema.extend({ chance: z.number().min(0).max(100).optional() })).max(200).optional(),
    lootedCount: nonNegInt.optional(),
    startsQuests: z.array(artifactRefSchema).max(100).optional(),
    endsQuests: z.array(artifactRefSchema).max(100).optional(),
    observed: observedSchema.optional(),
  })
  .strict();

export const zoneDataSchema = z
  .object({
    continent: shortText.optional(),
    levelMin: nonNegInt.max(70).optional(),
    levelMax: nonNegInt.max(70).optional(),
    faction: z.enum(['Alliance', 'Horde', 'Contested', 'PvP']).optional(),
    areaId: z.number().int().optional(),
    description: longText.optional(),
    /** Stitched zone map in our image storage (written by the map import, seed-managed). */
    mapUrl: z.string().url().optional(),
    /** Minimap composite and loading screen from the client (also written by the map import). */
    minimapUrl: z.string().url().optional(),
    loadingScreenUrl: z.string().url().optional(),
  })
  .strict();

/** One rank of a class ability, as taught in Classic Era (seed-managed). */
export const abilityRankSchema = z.object({
  rank: z.number().int().min(1),
  spellId: z.number().int(),
  level: nonNegInt.max(70).optional(),
  cost: shortText.optional(),
  castTime: shortText.optional(),
  cooldown: shortText.optional(),
  range: shortText.optional(),
  description: longText.optional(),
  /** Trainer price in copper. */
  trainerCost: nonNegInt.optional(),
}).strict();

export const abilityDataSchema = z
  .object({
    class: shortText.optional(),
    /** Skill line ("Fire", "Arms", "Restoration"). */
    tree: shortText.optional(),
    level: nonNegInt.max(70).optional(),
    rank: nonNegInt.optional(),
    cost: shortText.optional(),
    cooldown: shortText.optional(),
    castTime: shortText.optional(),
    range: shortText.optional(),
    description: longText.optional(),
    passive: z.boolean().optional(),
    /** Spell icon on Blizzard's render CDN (seed-managed). */
    iconUrl: z.string().url().optional(),
    ranks: z.array(abilityRankSchema).max(20).optional(),
    /** Trainer NPCs (seed-managed, resolved to npc artifacts). */
    taughtBy: z.array(artifactRefSchema).max(100).optional(),
    /** Quests whose reward teaches the ability (seed-managed). */
    learnedFrom: z.array(artifactRefSchema).max(10).optional(),
    /** Races that start with the ability. */
    startingFor: z.array(shortText).max(8).optional(),
    /** Talents are ability pages too; absent means a regular ability. */
    kind: z.enum(['talent']).optional(),
    /** Talent tree row and column, 1-based. */
    tier: z.number().int().min(1).max(10).optional(),
    column: z.number().int().min(1).max(6).optional(),
    /** Talent that must be taken first (seed-managed, resolved to an ability artifact). */
    prerequisite: artifactRefSchema.extend({ rank: z.number().int().min(1).optional() }).optional(),
    /**
     * Classic rank spell ids of a Forever talent (Classic had one spell per rank; Forever one
     * spell with ranks). Battle.net's Classic Era documents report these (seed-managed).
     */
    legacySpellIds: z.array(z.number().int()).max(20).optional(),
  })
  .strict();

/** What an enchant adds, as the client's enchantment tables state it (seed-managed). */
export const ENCHANT_EFFECT_KINDS = [
  'stat',
  'resistance',
  'attackPower',
  'spellPower',
  'healing',
  'manaRegen',
  'healthRegen',
  'haste',
  'crit',
  'proc',
  'aura',
  'damage',
  'use',
  'other',
] as const;
export const enchantSchema = z
  .object({
    /** SpellItemEnchantment id; a character's equipped enchant carries the same id. */
    id: nonNegInt,
    text: shortText,
    /** Equipment slots the enchant applies to ("Chest", "Main Hand", ...). */
    slots: z.array(shortText).max(20),
    weaponTypes: z.array(shortText).max(20).optional(),
    effects: z.array(z.object({ kind: z.enum(ENCHANT_EFFECT_KINDS), name: shortText, value: z.number().int().optional() })).max(6),
    temporary: z.boolean(),
  })
  .strict();
export type EnchantData = z.infer<typeof enchantSchema>;

export const recipeDataSchema = z
  .object({
    profession: shortText.optional(),
    requiredSkill: nonNegInt.optional(),
    /** Skill levels at which the recipe turns yellow, green and grey (seed-managed). */
    skillColors: z.object({ yellow: nonNegInt, green: nonNegInt, grey: nonNegInt }).optional(),
    /** Whether a trainer, a recipe item or both teach it (seed-managed). */
    taught: z.enum(['trainer', 'item', 'both']).optional(),
    source: shortText.optional(),
    /** Reagents as item refs; `count` is the reagent count (a player's `{ name, count }` is valid too). */
    reagents: z.array(artifactRefSchema).max(20).optional(),
    /** The recipe item (formula, pattern, ...) that teaches it (seed-managed). */
    recipeItem: artifactRefSchema.optional(),
    /** The item the recipe creates (seed-managed); `resultItemName` stays for player-written recipes. */
    resultItem: artifactRefSchema.optional(),
    resultItemName: shortText.optional(),
    enchant: enchantSchema.optional(),
    description: longText.optional(),
  })
  .strict();

export const artifactDataSchemas = {
  quest: questDataSchema,
  item: itemDataSchema,
  npc: npcDataSchema,
  object: objectDataSchema,
  zone: zoneDataSchema,
  ability: abilityDataSchema,
  recipe: recipeDataSchema,
} as const satisfies Record<ArtifactType, z.ZodTypeAny>;

export type QuestData = z.infer<typeof questDataSchema>;
export type ItemData = z.infer<typeof itemDataSchema>;
export type NpcData = z.infer<typeof npcDataSchema>;
export type ObjectData = z.infer<typeof objectDataSchema>;
export type ZoneData = z.infer<typeof zoneDataSchema>;
export type AbilityData = z.infer<typeof abilityDataSchema>;
export type RecipeData = z.infer<typeof recipeDataSchema>;
export type ArtifactData =
  | QuestData
  | ItemData
  | NpcData
  | ObjectData
  | ZoneData
  | AbilityData
  | RecipeData;

/** Validates `data` for a given artifact type; throws ZodError on failure. */
export function parseArtifactData(type: ArtifactType, data: unknown): ArtifactData {
  return artifactDataSchemas[type].parse(data) as ArtifactData;
}
